import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Input,
  Form,
  message,
  Tooltip,
  Typography,
  Row,
  Col,
  Alert,
} from 'antd';
import DatePicker from '../DatePicker';
import { InfoCircleOutlined } from '@ant-design/icons';
import {
  formatDate,
  validPageSplit,
  invalidPageSplit,
  invalidPageRange,
} from '../../global/validation';
import ModalWrapper from './ModalWrapper';

class SplitSelectedRecord extends Component {
  static propTypes = {
    hideModal: PropTypes.func.isRequired,
    modalType: PropTypes.object.isRequired,
    splitUploadedRecord: PropTypes.func.isRequired,
    splitReceivedRecord: PropTypes.func.isRequired,
    isLoading: PropTypes.bool.isRequired,
    error: PropTypes.bool.isRequired,
    errorMessage: PropTypes.string.isRequired,
  }

  state = {
    patientFirstName: this.props.modalType.data.patient.patient_first_name,
    patientLastName: this.props.modalType.data.patient.patient_last_name,
    patientMiddleName: this.props.modalType.data.patient.patient_middle_name,
    patientDOB: this.props.modalType.data.patient.patient_dob,
    fileToken: this.props.modalType.data.patient.file_token,
    pageToSplit: '',
    fromPage: this.props.modalType.data.fromPage,
    token: this.props.modalType.data.token,
    numPages: this.props.modalType.data.numPages,
  };

  split = async (event) => {
    event.preventDefault();
    const {
      fileToken,
      patientFirstName,
      patientLastName,
      patientMiddleName,
      patientDOB,
      pageToSplit,
      fromPage,
      token,
    } = this.state;

    const {
      splitUploadedRecord,
      splitReceivedRecord,
    } = this.props;

    const dob = patientDOB ? formatDate(patientDOB) : null;
    const isUploaded = fromPage === 'uploaded';

    // For uploaded records, show a "processing" message since it's async
    if (isUploaded) {
      message.loading({
        content: 'Splitting record in background...',
        key: 'splitLoading',
        duration: 0,
      });
    }

    const result = isUploaded
      ? await splitUploadedRecord(fileToken, pageToSplit, patientFirstName, patientLastName, patientMiddleName, dob)
      : await splitReceivedRecord(token, pageToSplit, patientFirstName, patientLastName, patientMiddleName, dob);

    // Destroy loading message for uploaded records
    if (isUploaded) {
      message.destroy('splitLoading');
    }

    this.displayMessage(result);
  };

  /**
   * @param {String} dateString format MM/DD/YYYY
   */
  handleDateChange = (_, dateString) => {
    this.setState({ patientDOB: dateString });
  }

  displayMessage(result) {
    const {
      pageToSplit,
      fromPage,
    } = this.state;

    const {
      hideModal,
      error,
      errorMessage,
    } = this.props;

    // Check for error from async result or from redux state
    const hasError = result?.error || error;
    const errorMsg = result?.message || errorMessage;

    if (!hasError) {
      message.success({
        content: `Move to your ${fromPage} inbox to view the split record(s)`,
      });
    } else {
      message.error({
        content: `Unable to split ${pageToSplit} page(s). ${errorMsg}`,
      });
    }
    hideModal();
  }

  render() {
    // eslint-disable-next-line object-curly-newline
    const { patientFirstName, patientMiddleName, patientLastName, pageToSplit, patientDOB, numPages } = this.state;
    const { isLoading, hideModal, modalType } = this.props;
    const { title, buttonText } = modalType.data;
    const invalidPageRanges = invalidPageRange(numPages, pageToSplit) || !validPageSplit.test(pageToSplit);

    // Validate name fields: if any name is provided, both first and last name must be filled
    const hasFirstName = patientFirstName && patientFirstName.trim().length > 0;
    const hasMiddleName = patientMiddleName && patientMiddleName.trim().length > 0;
    const hasLastName = patientLastName && patientLastName.trim().length > 0;
    const hasAnyName = hasFirstName || hasMiddleName || hasLastName;
    const invalidNameCombination = hasAnyName && !(hasFirstName && hasLastName);

    const dialogTitle = title || 'Split Record';
    const dialogButtonText = buttonText || 'Split';
    const customContentStyle = {
      width: '340px',
    };

    const dateFormat = ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY'];
    return (
      <ModalWrapper
        hideModal={hideModal}
        customContentStyle={customContentStyle}
        action={this.split}
        actionName={dialogButtonText}
        dismiss="Cancel"
        modalTitle={dialogTitle}
        isLoading={isLoading}
        disabled={invalidPageRanges || invalidNameCombination}
        isDraggable={true}
      >
        <div>
          {modalType.data.patient && (
            <Form
              layout="vertical"
              onFinish={this.split}
            >
              <div style={{
                marginBottom: '16px',
                padding: '12px 16px',
                background: 'linear-gradient(135deg, #fff7e6 0%, #fffbe6 100%)',
                borderLeft: '4px solid #faad14',
                borderRadius: '4px',
              }}>
                <div style={{ fontWeight: 600, color: '#d48806', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#d48806" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09m8.445-7.188L18 9.75l-.259-1.035a3.38 3.38 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.38 3.38 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.38 3.38 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.38 3.38 0 0 0-2.456 2.456m-1.365 11.852L16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183l.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394l-1.183.394a2.25 2.25 0 0 0-1.423 1.423" />
                  </svg>
                  New Format
                </div>
                <div style={{ color: '#595959', fontSize: '13px', lineHeight: '1.5' }}>
                  Now uses print-style ranges: <strong>1-3</strong> (pages 1-3), <strong>5</strong> (page 5 only), <strong>10-</strong> (page 10 to end), <strong>-5</strong> (pages 1-5)
                </div>
              </div>
              <Row gutter={8}>
                <Col span={8}>
                  <Form.Item
                    label="First Name (optional)"
                    name="patientFirstName"
                  >
                    <Input
                      placeholder="First Name"
                      onChange={(e) => this.setState({
                        patientFirstName: e.target.value,
                      })}
                      defaultValue={patientFirstName}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="Middle Name (optional)"
                    name="patientMiddleName"
                  >
                    <Input
                      placeholder="Middle Name"
                      onChange={(e) => this.setState({
                        patientMiddleName: e.target.value,
                      })}
                      defaultValue={patientMiddleName}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="Last Name (optional)"
                    name="patientLastName"
                  >
                    <Input
                      placeholder="Last Name"
                      onChange={(e) => this.setState({
                        patientLastName: e.target.value,
                      })}
                      defaultValue={patientLastName}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item
                label="Date of Birth MM/DD/YYYY (optional)"
                name="optionalPatientDOB"
              >
                <DatePicker
                  onChange={this.handleDateChange}
                  name="patientDOB"
                  format={dateFormat}
                  defaultValue={patientDOB && moment(patientDOB, dateFormat)}
                />
              </Form.Item>
              <Form.Item
                hasFeedback
                required
                name="pageNumber"
                label={(
                  <Tooltip
                    placement="left"
                    title={(
                      <span style={{ whiteSpace: 'pre-line' }}>
                        {`Enter the pages you want to extract as separate documents using standard print range format:
                          \n• Single page: 5 (extracts only page 5)
                          \n• Page range: 1-3 (extracts pages 1, 2, and 3)
                          \n• From page to end: 10- (extracts page 10 to last page)
                          \n• From start to page: -5 (extracts pages 1 to 5)
                          \n• Multiple selections: 1-3, 5, 7-10 (creates 3 documents)
                          \nPages not specified will not be included in the new documents.`
                        }
                      </span>
                )}
                  >
                    Enter Page Range(s) to Extract
                    <InfoCircleOutlined style={{ paddingLeft: '2px', marginTop: '6px', height: '8px' }} />
                  </Tooltip>
                )}
                rules={[
                  () => ({
                    validator(_, number) {
                      if (invalidPageRange(numPages, number)) return Promise.reject('Enter a valid page range within the document');
                      if (!validPageSplit.test(number)) return Promise.reject(invalidPageSplit);
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input
                  name="pageNumber"
                  data-tutorial="split-pages"
                  placeholder="e.g. 1-3, 5, 7-10"
                  onChange={e => this.setState({ pageToSplit: e.target.value })}
                  defaultValue={pageToSplit}
                  style={{ width: '100%' }}
                />
              </Form.Item>
              <Typography.Text>
                <Typography.Text type="danger">*</Typography.Text> Remember to save the document before splitting
              </Typography.Text>
            </Form>
          )}
        </div>
      </ModalWrapper>
    );
  }
}

export default SplitSelectedRecord;
